/**
 * Scheduled Broadcasts Management
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, X, Megaphone, Play, Clock, ToggleLeft, ToggleRight } from 'lucide-react';
import axios from 'axios';

const DAY_LABELS = ['Dom','Lun','Mar','Mié','Jue','Vie','Sáb'];

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.40)', backdropFilter: 'blur(4px)' }}>
      <div className="w-full max-w-lg rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
        style={{ background: 'rgba(255,255,255,0.96)', border: '1px solid rgba(0,122,255,0.20)' }}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-bold text-n-800 text-base">{title}</h3>
          <button onClick={onClose} className="text-n-500 hover:text-n-700"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ToggleSwitch({ checked, onChange }) {
  return (
    <button onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${checked ? 'bg-s-blue' : 'bg-n-300'}`}>
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  );
}

const emptyForm = {
  name: '',
  message: '',
  channelIds: [],
  scheduleType: 'once',
  scheduledAt: new Date(Date.now() + 3600000).toISOString().slice(0, 16),
  daysOfWeek: [],
  active: true,
};

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString();
}

export default function Broadcasts() {
  const [broadcasts, setBroadcasts] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [sendingNow, setSendingNow] = useState(null);

  const load = useCallback(async () => {
    try {
      const [br, ch] = await Promise.all([
        axios.get('/api/admin/broadcasts'),
        axios.get('/api/admin/channels'),
      ]);
      setBroadcasts(br.data);
      setChannels(ch.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm(emptyForm);
    setErr('');
    setModal('create');
  };

  const openEdit = (b) => {
    setSelected(b);
    setForm({
      name: b.name,
      message: b.message,
      channelIds: b.channelIds || [],
      scheduleType: b.scheduleType || 'once',
      scheduledAt: new Date(b.scheduledAt).toISOString().slice(0, 16),
      daysOfWeek: b.daysOfWeek || [],
      active: b.active,
    });
    setErr('');
    setModal('edit');
  };

  const openDelete = (b) => { setSelected(b); setErr(''); setModal('delete'); };

  const toggleChannel = (id) => {
    setForm(f => ({
      ...f,
      channelIds: f.channelIds.includes(id)
        ? f.channelIds.filter(c => c !== id)
        : [...f.channelIds, id],
    }));
  };

  const toggleDay = (d) => {
    setForm(f => ({
      ...f,
      daysOfWeek: f.daysOfWeek.includes(d)
        ? f.daysOfWeek.filter(x => x !== d)
        : [...f.daysOfWeek, d],
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.message.trim() || !form.scheduledAt) {
      setErr('Nombre, mensaje y fecha son requeridos');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, scheduledAt: new Date(form.scheduledAt).toISOString() };
      if (modal === 'create') {
        await axios.post('/api/admin/broadcasts', payload);
      } else {
        await axios.put(`/api/admin/broadcasts/${selected.id}`, payload);
      }
      await load();
      setModal(null);
    } catch (e) {
      setErr(e.response?.data?.error || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await axios.delete(`/api/admin/broadcasts/${selected.id}`);
      await load();
      setModal(null);
    } catch (e) {
      setErr(e.response?.data?.error || 'Error');
    } finally { setSaving(false); }
  };

  const handleSendNow = async (b) => {
    setSendingNow(b.id);
    try {
      await axios.post(`/api/admin/broadcasts/${b.id}/send-now`);
      await load();
    } catch (e) { console.error(e); }
    finally { setSendingNow(null); }
  };

  const toggleActive = async (b) => {
    try {
      await axios.put(`/api/admin/broadcasts/${b.id}`, { active: !b.active });
      await load();
    } catch (e) { console.error(e); }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-n-800">Broadcasts Programados</h1>
          <p className="text-sm text-n-500 mt-0.5">Mensajes automáticos a canales en horarios definidos</p>
        </div>
        <button onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white shadow-apple-sm transition-all hover:opacity-90"
          style={{ background: 'linear-gradient(135deg,#007AFF,#5856D6)' }}>
          <Plus size={15} /> Nuevo Broadcast
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-n-500 text-sm">Cargando...</div>
      ) : broadcasts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-n-500 text-sm">
          <Megaphone size={32} className="mb-3 opacity-30" />
          <p>No hay broadcasts programados</p>
        </div>
      ) : (
        <div className="space-y-3">
          {broadcasts.map(b => (
            <div key={b.id} className="px-5 py-4 rounded-2xl"
              style={{ background: 'rgba(255,255,255,0.82)', border: `1px solid ${b.active ? 'rgba(255,211,61,0.15)' : 'rgba(255,255,255,0.04)'}` }}>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{ background: 'rgba(255,211,61,0.1)' }}>
                  <Megaphone size={16} style={{ color: '#FF9500' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-n-800 text-sm">{b.name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${b.scheduleType === 'once' ? 'text-n-600 bg-n-150' : b.scheduleType === 'daily' ? 'text-s-blue bg-s-blue/10' : 'text-purple-400 bg-purple-500/10'}`}>
                      {b.scheduleType === 'once' ? 'Una vez' : b.scheduleType === 'daily' ? 'Diario' : 'Semanal'}
                    </span>
                  </div>
                  <p className="text-xs text-n-600 mt-1 line-clamp-2">{b.message}</p>
                  <div className="flex items-center gap-3 mt-2 text-xs text-n-500 flex-wrap">
                    <span className="flex items-center gap-1"><Clock size={10} /> {fmtDate(b.scheduledAt)}</span>
                    <span>{(b.channelIds || []).length} canal(es)</span>
                    {b.lastSentAt && <span>Último envío: {fmtDate(b.lastSentAt)}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <ToggleSwitch checked={b.active} onChange={() => toggleActive(b)} />
                  <button onClick={() => handleSendNow(b)} disabled={sendingNow === b.id}
                    className="p-2 rounded-lg text-n-500 hover:text-s-orange hover:bg-s-orange/10 transition-all disabled:opacity-50" title="Enviar ahora">
                    <Play size={13} />
                  </button>
                  <button onClick={() => openEdit(b)}
                    className="p-2 rounded-lg text-n-500 hover:text-s-blue hover:bg-s-blue/10 transition-all">
                    <Edit2 size={13} />
                  </button>
                  <button onClick={() => openDelete(b)}
                    className="p-2 rounded-lg text-n-500 hover:text-s-red hover:bg-s-red/10 transition-all">
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit Modal */}
      {(modal === 'create' || modal === 'edit') && (
        <Modal title={modal === 'create' ? 'Nuevo Broadcast' : 'Editar Broadcast'} onClose={() => setModal(null)}>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-n-600 uppercase tracking-wider mb-1.5">Nombre</label>
              <input className="w-full px-4 py-2.5 rounded-xl text-sm text-n-800 placeholder-n-400 outline-none"
                style={{ background: 'rgba(255,255,255,0.78)', border: '1px solid rgba(0,122,255,0.20)' }}
                placeholder="Turno matutino" value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-n-600 uppercase tracking-wider mb-1.5">Mensaje</label>
              <textarea rows={3} className="w-full px-4 py-2.5 rounded-xl text-sm text-n-800 placeholder-n-400 outline-none resize-none"
                style={{ background: 'rgba(255,255,255,0.78)', border: '1px solid rgba(0,122,255,0.20)' }}
                placeholder="Inicio de turno — todos los equipos listos"
                value={form.message}
                onChange={e => setForm(f => ({ ...f, message: e.target.value }))} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-n-600 uppercase tracking-wider mb-1.5">Canales</label>
              <div className="max-h-36 overflow-y-auto space-y-1">
                {channels.map(ch => (
                  <label key={ch.id} className="flex items-center gap-2 px-3 py-1.5 rounded-lg cursor-pointer hover:bg-white/5">
                    <input type="checkbox" checked={form.channelIds.includes(ch.id)}
                      onChange={() => toggleChannel(ch.id)} className="w-4 h-4 rounded accent-brand-500" />
                    <span className="text-sm text-n-700">{ch.name}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-n-600 uppercase tracking-wider mb-1.5">Tipo</label>
              <div className="flex gap-2">
                {['once','daily','weekly'].map(t => (
                  <button key={t} onClick={() => setForm(f => ({ ...f, scheduleType: t }))}
                    className={`flex-1 py-2 rounded-xl text-xs font-semibold transition-all ${form.scheduleType === t ? 'text-n-800' : 'text-n-500 border border-slate-700 hover:border-slate-500'}`}
                    style={form.scheduleType === t ? { background: '#007AFF' } : {}}>
                    {t === 'once' ? 'Una vez' : t === 'daily' ? 'Diario' : 'Semanal'}
                  </button>
                ))}
              </div>
            </div>
            {form.scheduleType === 'weekly' && (
              <div>
                <label className="block text-xs font-semibold text-n-600 uppercase tracking-wider mb-1.5">Días de la semana</label>
                <div className="flex gap-1.5 flex-wrap">
                  {DAY_LABELS.map((d, i) => (
                    <button key={i} onClick={() => toggleDay(i)}
                      className={`w-10 h-8 rounded-lg text-xs font-semibold transition-all ${form.daysOfWeek.includes(i) ? 'text-n-800' : 'text-n-500 border border-slate-700'}`}
                      style={form.daysOfWeek.includes(i) ? { background: '#007AFF' } : {}}>
                      {d}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div>
              <label className="block text-xs font-semibold text-n-600 uppercase tracking-wider mb-1.5">
                {form.scheduleType === 'once' ? 'Fecha y hora' : 'Hora'}
              </label>
              <input type="datetime-local" value={form.scheduledAt}
                onChange={e => setForm(f => ({ ...f, scheduledAt: e.target.value }))}
                className="w-full px-4 py-2.5 rounded-xl text-sm text-n-800 outline-none"
                style={{ background: 'rgba(255,255,255,0.78)', border: '1px solid rgba(0,122,255,0.20)' }} />
            </div>
            <div className="flex items-center justify-between px-1">
              <span className="text-sm text-n-600">Activo</span>
              <ToggleSwitch checked={form.active} onChange={v => setForm(f => ({ ...f, active: v }))} />
            </div>
            {err && <p className="text-xs text-s-red">{err}</p>}
            <div className="flex gap-2 pt-2">
              <button onClick={() => setModal(null)}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-n-600 border border-slate-700 hover:border-slate-500 transition-all">
                Cancelar
              </button>
              <button onClick={handleSave} disabled={saving}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-n-800 disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg,#007AFF,#5856D6)' }}>
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete Modal */}
      {modal === 'delete' && (
        <Modal title="Eliminar Broadcast" onClose={() => setModal(null)}>
          <div className="space-y-4">
            <p className="text-sm text-n-600">¿Eliminar <span className="text-n-800 font-semibold">{selected?.name}</span>?</p>
            {err && <p className="text-xs text-s-red">{err}</p>}
            <div className="flex gap-2 pt-2">
              <button onClick={() => setModal(null)}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-n-600 border border-slate-700">Cancelar</button>
              <button onClick={handleDelete} disabled={saving}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg,#FF375F,#FF3B30)' }}>
                {saving ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
